import React, {useEffect, useMemo, useState} from "react";
import {observer} from "mobx-react-lite";
import {useHistory, useLocation, useParams} from "react-router-dom";
import Helmet from "react-helmet";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {makeStyles} from "@material-ui/core/styles";
import {
    AppBar,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Container,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Toolbar,
    Typography
} from "@material-ui/core";
import {Alert} from "@material-ui/lab";
import {useStores} from "../../hooks/use-stores";
import services from "../../services";
import {extractErrorMessage, patchCDN} from "../../utils/helpers";
import {setCookie as persistCookie} from "../../utils/cookieUtil";

const CHECKOUT_URL = "https://my.lifeinfoapp.com/checkout";

const useStyles = makeStyles((theme) => ({
    page: {
        minHeight: "100vh",
        background: "black",
        color: "white",
        display: "flex",
        flexDirection: "column"
    },
    appBar: {
        backgroundColor: "black",
        borderBottom: "1px solid rgba(255,255,255,0.12)"
    },
    logo: {
        width: 40,
        marginRight: theme.spacing(1.5)
    },
    title: {
        textTransform: "uppercase",
        letterSpacing: 1,
        fontWeight: 600
    },
    content: {
        flex: 1,
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(8)
    },
    card: {
        backgroundColor: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 16,
        padding: theme.spacing(4),
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(3)
        }
    },
    image: {
        width: 140,
        height: 140,
        objectFit: "contain",
        borderRadius: 12,
        backgroundColor: "rgba(255,255,255,0.04)"
    },
    productName: {
        fontWeight: 700,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1)
    },
    price: {
        color: theme.palette.primary.main,
        fontSize: 32,
        fontWeight: 700,
        lineHeight: 1.2
    },
    pv: {
        color: "rgba(255,255,255,0.7)",
        fontWeight: 600,
        marginLeft: theme.spacing(2)
    },
    description: {
        marginTop: theme.spacing(3),
        color: "rgba(255,255,255,0.85)",
        lineHeight: 1.6,
        "& p": {
            marginTop: 0
        }
    },
    selector: {
        marginTop: theme.spacing(3),
        minWidth: 220,
        "& .MuiInputBase-root": {
            color: "white"
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255,255,255,0.24)"
        },
        "& .MuiInputLabel-root": {
            color: "rgba(255,255,255,0.7)"
        }
    },
    notice: {
        marginTop: theme.spacing(3)
    },
    ackBox: {
        marginTop: theme.spacing(4),
        backgroundColor: "rgba(0,123,255,0.08)",
        border: "1px solid rgba(0,123,255,0.35)",
        borderRadius: 12,
        padding: theme.spacing(2.5)
    },
    ackTitle: {
        fontWeight: 700,
        marginBottom: theme.spacing(1.5)
    },
    ackBody: {
        color: "rgba(255,255,255,0.88)",
        lineHeight: 1.6,
        "& p": {
            marginTop: 0,
            marginBottom: theme.spacing(1.5)
        }
    },
    ackCheck: {
        alignItems: "flex-start",
        marginTop: theme.spacing(1),
        "& .MuiFormControlLabel-label": {
            color: "white"
        }
    },
    checkoutButton: {
        marginTop: theme.spacing(3),
        paddingTop: 12,
        paddingBottom: 12,
        fontSize: 16,
        fontWeight: 700
    },
    centered: {
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    }
}));

function normalizeProducts(data) {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.products)) return data.products;
    if (typeof data.products === "string") {
        try {
            const parsed = JSON.parse(data.products);
            return Array.isArray(parsed) ? parsed : (parsed ? [parsed] : []);
        } catch {
            return [];
        }
    }
    if (data.products && typeof data.products === "object") {
        const value = Object.values(data.products)[0];
        if (Array.isArray(value)) return value;
        if (typeof value === "string") {
            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : (parsed ? [parsed] : []);
            } catch {
                return [];
            }
        }
    }
    return [];
}

function formatPrice(price) {
    const value = Number(price);
    if (Number.isNaN(value)) return price || "";
    return `$${value.toFixed(2)}`;
}

export default observer(function QuickCheckout() {
    const classes = useStyles();
    const {t} = useTranslation("subscription");
    const location = useLocation();
    const history = useHistory();
    const params = useParams();
    const {authStore, commonStore, shopStore} = useStores();
    const {language} = commonStore;
    const {currentUser} = authStore;

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const skuParam = searchParams.get("sku");
    const categoryID = useMemo(() => {
        const fromRoute = params.categoryID;
        const fromQuery = searchParams.get("id") || searchParams.get("categoryID") || searchParams.get("categoryId");
        const raw = fromRoute || fromQuery;
        const parsed = parseInt(raw, 10);
        return Number.isNaN(parsed) ? null : parsed;
    }, [params.categoryID, searchParams]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [products, setProducts] = useState([]);
    const [selectedProductID, setSelectedProductID] = useState("");
    const [acknowledged, setAcknowledged] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const preferredLanguage = currentUser && currentUser.preferredLanguage;
        if (!preferredLanguage) return;
        commonStore.setLanguage(preferredLanguage, true);
    }, [currentUser, commonStore]);

    useEffect(() => {
        if (!searchParams.has("login") && !searchParams.has("loginguid")) return;
        const next = new URLSearchParams(searchParams);
        next.delete("login");
        next.delete("loginguid");
        history.replace({pathname: location.pathname, search: next.toString()});
    }, [history, location.pathname, searchParams]);

    useEffect(() => {
        if (!currentUser || !categoryID) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            setAcknowledged(false);
            try {
                const result = await services.ShoppingCart.getQuickCheckout(categoryID);
                if (cancelled) return;
                const list = normalizeProducts(result).slice().sort((a, b) => {
                    const yearDiff = Number(a.subYear || 0) - Number(b.subYear || 0);
                    if (yearDiff !== 0) return yearDiff;
                    return Number(a.subMonth || 0) - Number(b.subMonth || 0);
                });
                setProducts(list);
                const skuMatch = skuParam
                    ? list.find((item) => String(item.sku || "").toLowerCase() === skuParam.toLowerCase())
                    : null;
                setSelectedProductID(String((skuMatch || list[0] || {}).productID || ""));
            } catch (e) {
                if (!cancelled) {
                    setProducts([]);
                    setError(extractErrorMessage(e) || t("somethingWentWrong"));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [currentUser, categoryID, skuParam, t]);

    const selectedProduct = useMemo(() => {
        return products.find((item) => String(item.productID) === String(selectedProductID)) || products[0] || null;
    }, [products, selectedProductID]);

    const alreadySubscribed = !!(selectedProduct && selectedProduct.isSubscribed);
    const allAcks = t("ack", {returnObjects: true});
    const ack = (allAcks && typeof allAcks === "object")
        ? (allAcks[categoryID] || allAcks[String(categoryID)])
        : null;
    const ackBody = ack && typeof ack === "object" ? ack.body : null;
    const ackCheckbox = (ack && typeof ack === "object" && ack.checkbox) ? ack.checkbox : t("ack_checkbox_fallback");
    const monthLabel = (product) => {
        if (!product || product.subMonth == null) return product?.sku || "";
        const year = Number(product.subYear);
        const monthDate = Number.isFinite(year) && year > 0
            ? moment({year, month: Number(product.subMonth) - 1})
            : moment().month(Number(product.subMonth) - 1);
        const monthName = monthDate.format("MMMM");
        const translated = t(`global:${monthName}`, {defaultValue: monthName});
        return t("monthTicket", {month: translated, defaultValue: `${translated} Ticket`});
    };

    const handleCheckout = async () => {
        if (!selectedProduct || alreadySubscribed || submitting) return;
        if (!acknowledged) return;
        setSubmitting(true);
        setError(null);
        try {
            const result = await services.ShoppingCart.addItemToCart({
                productID: selectedProduct.productID,
                qty: 1,
                lang: (currentUser && currentUser.preferredLanguage) || language || "en",
                cartID: shopStore.userCartGuid || ""
            });
            const cartID = result && result.cartID;
            if (!cartID) {
                throw new Error(t("somethingWentWrong"));
            }
            persistCookie("cart_guid", cartID);
            window.location.replace(`${CHECKOUT_URL}?cart_guid=${cartID}`);
        } catch (e) {
            setError(extractErrorMessage(e) || t("somethingWentWrong"));
            setSubmitting(false);
        }
    };

    return (
        <div className={classes.page}>
            <Helmet>
                <title>{t("checkout")} | Starfish</title>
            </Helmet>
            <AppBar position="static" elevation={0} className={classes.appBar}>
                <Toolbar>
                    <img className={classes.logo} src="/starfish.png" alt="Starfish" />
                    <Typography variant="h6" className={classes.title}>{t("checkout")}</Typography>
                </Toolbar>
            </AppBar>
            <Container maxWidth="sm" className={classes.content}>
                {!currentUser && (
                    <div className={classes.centered}><CircularProgress /></div>
                )}
                {currentUser && !categoryID && (
                    <Alert severity="error">{t("missingCategory")}</Alert>
                )}
                {currentUser && categoryID && loading && (
                    <div className={classes.centered}><CircularProgress /></div>
                )}
                {currentUser && categoryID && !loading && error && products.length === 0 && (
                    <Alert severity="error">{error}</Alert>
                )}
                {currentUser && categoryID && !loading && !error && products.length === 0 && (
                    <Alert severity="warning">{t("thisProductIsNotAvailable")}</Alert>
                )}
                {currentUser && selectedProduct && !loading && (
                    <Box className={classes.card}>
                        <Grid container direction="column" alignItems="center">
                            {selectedProduct.imageURL && (
                                <img
                                    className={classes.image}
                                    src={patchCDN(selectedProduct.imageURL)}
                                    alt={selectedProduct.categoryName || ""}
                                />
                            )}
                            <Typography variant="h4" align="center" className={classes.productName}>
                                {selectedProduct.categoryName}
                            </Typography>
                            <Box display="flex" alignItems="baseline" justifyContent="center">
                                <Typography className={classes.price}>{formatPrice(selectedProduct.price)}</Typography>
                                {selectedProduct.pv != null && (
                                    <Typography className={classes.pv}>
                                        {t("pv")} {Number(selectedProduct.pv)}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        {products.length > 1 && (
                            <FormControl variant="outlined" className={classes.selector} fullWidth>
                                <InputLabel id="quickcheckout-product-label">{t("selectMonth")}</InputLabel>
                                <Select
                                    labelId="quickcheckout-product-label"
                                    label={t("selectMonth")}
                                    value={String(selectedProductID)}
                                    onChange={(event) => {
                                        setSelectedProductID(String(event.target.value));
                                        setAcknowledged(false);
                                    }}
                                >
                                    {products.map((product) => (
                                        <MenuItem key={product.productID} value={String(product.productID)}>
                                            {monthLabel(product)} — {formatPrice(product.price)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {selectedProduct.categoryDesc && (
                            <Typography
                                className={classes.description}
                                component="div"
                                dangerouslySetInnerHTML={{__html: selectedProduct.categoryDesc}}
                            />
                        )}

                        {selectedProduct.requireName && (
                            <Alert className={classes.notice} severity="info">
                                {t("subscriptionRequires", {name: selectedProduct.requireName})}
                            </Alert>
                        )}

                        {alreadySubscribed && (
                            <Alert className={classes.notice} severity="warning">
                                {t("alreadySubscribed")}
                            </Alert>
                        )}

                        {!alreadySubscribed && (
                            <Box className={classes.ackBox}>
                                <Typography className={classes.ackTitle}>{t("subscriptionAcknowledgement")}</Typography>
                                {ackBody && (
                                    <Typography
                                        className={classes.ackBody}
                                        component="div"
                                        dangerouslySetInnerHTML={{__html: ackBody}}
                                    />
                                )}
                                <FormControlLabel
                                    className={classes.ackCheck}
                                    control={
                                        <Checkbox
                                            color="primary"
                                            checked={acknowledged}
                                            onChange={(event) => setAcknowledged(event.target.checked)}
                                        />
                                    }
                                    label={ackCheckbox}
                                />
                            </Box>
                        )}

                        {error && !loading && <Alert className={classes.notice} severity="error">{error}</Alert>}

                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            className={classes.checkoutButton}
                            disabled={alreadySubscribed || submitting || !acknowledged || !selectedProduct}
                            onClick={handleCheckout}
                        >
                            {submitting ? <CircularProgress size={22} color="inherit" /> : t("proceed_to_checkout")}
                        </Button>
                    </Box>
                )}
            </Container>
        </div>
    );
});
